import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { adminApi } from "../../../lib/admin";
import type { AdminBusinessListingListResponse, AdminGigListResponse } from "../../../types/adminTypes";
import type { PropertyPredicateDto } from "../../../types/gigTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { endOfLocalDay, parseLocalDateInput, startOfLocalDay, toIsoTimestamp } from "../../../utils/DateUtils";
import Button from "../../ui/Button/Button";
import Modal from "../../ui/Modal/Modal";
import AdminBusinessListingsTable from "./AdminBusinessListingsTable";
import AdminGigsFilters from "./AdminGigsFilters";
import AdminGigsTable from "./AdminGigsTable";

const DEFAULT_PAGE_SIZE = 10;
type AdminGigContentType = "GIGS" | "BUSINESS_LISTINGS";

const AdminGigsPanel = () => {
  const [contentType, setContentType] = useState<AdminGigContentType>("GIGS");
  const [search, setSearch] = useState("");
  const [poster, setPoster] = useState("");
  const [status, setStatus] = useState("PUBLISHED");
  const [createdFromDate, setCreatedFromDate] = useState("");
  const [createdToDate, setCreatedToDate] = useState("");
  const [startFromDate, setStartFromDate] = useState("");
  const [startToDate, setStartToDate] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isCountModalOpen, setIsCountModalOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedPoster = useDebouncedValue(poster, 300);

  const predicates = useMemo(() => {
    const nextPredicates: PropertyPredicateDto[] = [];
    const createdFrom = parseLocalDateInput(createdFromDate);
    const createdTo = parseLocalDateInput(createdToDate);
    const startFrom = parseLocalDateInput(startFromDate);
    const startTo = parseLocalDateInput(startToDate);
    if (debouncedSearch.trim().length > 0) {
      nextPredicates.push({ propertyName: "search", propertyValue: debouncedSearch.trim(), operation: "CONTAINS" });
    }
    if (debouncedPoster.trim().length > 0) {
      nextPredicates.push({ propertyName: "poster", propertyValue: debouncedPoster.trim(), operation: "CONTAINS" });
    }
    if (status.trim().length > 0) {
      nextPredicates.push({ propertyName: "status", propertyValue: status.trim(), operation: "EQUALS" });
    }
    if (createdFrom) {
      nextPredicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(startOfLocalDay(createdFrom)), operation: "TIMESTAMP_FROM" });
    }
    if (createdTo) {
      nextPredicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(endOfLocalDay(createdTo)), operation: "TIMESTAMP_TO" });
    }
    if (contentType === "GIGS" && startFrom) {
      nextPredicates.push({ propertyName: "startTime", propertyValue: toIsoTimestamp(startOfLocalDay(startFrom)), operation: "TIMESTAMP_FROM" });
    }
    if (contentType === "GIGS" && startTo) {
      nextPredicates.push({ propertyName: "startTime", propertyValue: toIsoTimestamp(endOfLocalDay(startTo)), operation: "TIMESTAMP_TO" });
    }
    return nextPredicates;
  }, [contentType, createdFromDate, createdToDate, debouncedPoster, debouncedSearch, startFromDate, startToDate, status]);

  useEffect(() => {
    setTotalCount(null);
  }, [predicates]);

  const appliedFilters = useMemo(() => {
    const filters: Array<{ label: string; value: string }> = [];
    if (debouncedSearch.trim().length > 0) filters.push({ label: "Search", value: debouncedSearch.trim() });
    if (debouncedPoster.trim().length > 0) filters.push({ label: "Poster", value: debouncedPoster.trim() });
    if (status.trim().length > 0) filters.push({ label: "Status", value: status.trim() });
    if (createdFromDate.trim().length > 0) filters.push({ label: "Created from", value: createdFromDate });
    if (createdToDate.trim().length > 0) filters.push({ label: "Created to", value: createdToDate });
    if (contentType === "GIGS" && startFromDate.trim().length > 0) filters.push({ label: "Start from", value: startFromDate });
    if (contentType === "GIGS" && startToDate.trim().length > 0) filters.push({ label: "Start to", value: startToDate });
    return filters;
  }, [contentType, createdFromDate, createdToDate, debouncedPoster, debouncedSearch, startFromDate, startToDate, status]);

  const buildRequestPayload = () => ({
    predicates,
    page: {
      number: page,
      size: pageSize,
      numberOfElements: null,
      hasNext: false,
      hasPrevious: false,
      first: false,
      last: false,
      sortBy: [{ direction: "DESC" as const, propertyName: "createdAt" }],
    },
  });

  const totalCountMutation = useMutation({
    mutationFn: () => (
      contentType === "GIGS"
        ? adminApi.countGigs(buildRequestPayload())
        : adminApi.countBusinessListings(buildRequestPayload())
    ),
    onSuccess: (response) => {
      setTotalCount(response.totalCount);
    },
  });

  const listingsQuery = useQuery<AdminGigListResponse | AdminBusinessListingListResponse>({
    queryKey: ["admin-gigs", { contentType, page, pageSize, debouncedSearch, debouncedPoster, status, createdFromDate, createdToDate, startFromDate, startToDate }],
    queryFn: () => (
      contentType === "GIGS"
        ? adminApi.listGigs(buildRequestPayload())
        : adminApi.listBusinessListings(buildRequestPayload())
    ),
    placeholderData: (previousData: AdminGigListResponse | AdminBusinessListingListResponse | undefined) => previousData,
  });

  const handleContentTypeChange = (value: AdminGigContentType) => {
    setContentType(value);
    setStatus("PUBLISHED");
    setStartFromDate("");
    setStartToDate("");
    setPage(0);
  };

  const commonFilters = (
    <AdminGigsFilters
      contentType={contentType}
      search={search}
      poster={poster}
      status={status}
      createdFromDate={createdFromDate}
      createdToDate={createdToDate}
      startFromDate={startFromDate}
      startToDate={startToDate}
      pageSize={pageSize}
      onSearchChange={(value) => {
        setSearch(value);
        setPage(0);
      }}
      onPosterChange={(value) => {
        setPoster(value);
        setPage(0);
      }}
      onStatusChange={(value) => {
        setStatus(value);
        setPage(0);
      }}
      onCreatedFromDateChange={(value) => {
        setCreatedFromDate(value);
        setPage(0);
      }}
      onCreatedToDateChange={(value) => {
        setCreatedToDate(value);
        setPage(0);
      }}
      onStartFromDateChange={(value) => {
        setStartFromDate(value);
        setPage(0);
      }}
      onStartToDateChange={(value) => {
        setStartToDate(value);
        setPage(0);
      }}
      onPageSizeChange={(value) => {
        setPageSize(value);
        setPage(0);
      }}
      onRequestTotalCount={() => setIsCountModalOpen(true)}
      onContentTypeChange={handleContentTypeChange}
    />
  );

  const error = listingsQuery.error
    ? apiErrorToMessage(listingsQuery.error, contentType === "GIGS" ? "Failed to load gigs." : "Failed to load business listings.")
    : totalCountMutation.error
      ? apiErrorToMessage(totalCountMutation.error, "Failed to load the filtered count.")
      : null;

  const pageMetadata = listingsQuery.data?.page;
  const gigRows = listingsQuery.data && "gigs" in listingsQuery.data ? listingsQuery.data.gigs : [];
  const businessListingRows = listingsQuery.data && "listings" in listingsQuery.data ? listingsQuery.data.listings : [];

  return (
    <div>
      {error ? <p className="mb-4 text-sm font-medium text-red-600">{error}</p> : null}
      {contentType === "GIGS" ? (
        <AdminGigsTable
          gigs={gigRows}
          isLoading={listingsQuery.isLoading}
          contentType={contentType}
          search={search}
          poster={poster}
          status={status}
          createdFromDate={createdFromDate}
          createdToDate={createdToDate}
          startFromDate={startFromDate}
          startToDate={startToDate}
          pageSize={pageSize}
          page={page}
          resultsCount={pageMetadata?.numberOfElements ?? 0}
          hasNextPage={pageMetadata?.hasNext ?? false}
          hasPreviousPage={pageMetadata?.hasPrevious ?? false}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(0);
          }}
          onPosterChange={(value) => {
            setPoster(value);
            setPage(0);
          }}
          onStatusChange={(value) => {
            setStatus(value);
            setPage(0);
          }}
          onCreatedFromDateChange={(value) => {
            setCreatedFromDate(value);
            setPage(0);
          }}
          onCreatedToDateChange={(value) => {
            setCreatedToDate(value);
            setPage(0);
          }}
          onStartFromDateChange={(value) => {
            setStartFromDate(value);
            setPage(0);
          }}
          onStartToDateChange={(value) => {
            setStartToDate(value);
            setPage(0);
          }}
          onPageSizeChange={(value) => {
            setPageSize(value);
            setPage(0);
          }}
          onPageChange={setPage}
          onRequestTotalCount={() => setIsCountModalOpen(true)}
          onContentTypeChange={handleContentTypeChange}
        />
      ) : (
        <AdminBusinessListingsTable
          listings={businessListingRows}
          isLoading={listingsQuery.isLoading}
          page={page}
          pageSize={pageSize}
          resultsCount={pageMetadata?.numberOfElements ?? 0}
          hasNextPage={pageMetadata?.hasNext ?? false}
          hasPreviousPage={pageMetadata?.hasPrevious ?? false}
          filters={commonFilters}
          onPageChange={setPage}
        />
      )}

      <Modal isOpen={isCountModalOpen} onClose={() => setIsCountModalOpen(false)} title="Get total count" panelClassName="max-w-xl">
        <div className="space-y-5">
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
            <p className="text-sm text-myteengig-text-primary">
              This action will count {contentType === "GIGS" ? "gigs" : "business listings"} that match the filters currently applied on the page.
            </p>
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Applied filters</p>
            {appliedFilters.length > 0 ? (
              <div className="mt-3 space-y-2">
                {appliedFilters.map((filter) => (
                  <div key={filter.label} className="flex items-start justify-between gap-4 border-b border-myteengig-border/70 pb-2 last:border-b-0 last:pb-0">
                    <span className="text-sm font-medium text-myteengig-text-muted">{filter.label}</span>
                    <span className="text-right text-sm text-myteengig-text-primary">{filter.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-myteengig-text-muted">No filters are applied.</p>
            )}
          </div>

          {totalCount !== null ? (
            <div className="rounded-2xl border border-myteengig-border bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                Total {contentType === "GIGS" ? "gigs" : "business listings"}
              </p>
              <p className="mt-2 text-2xl font-semibold text-myteengig-text-primary">{totalCount}</p>
            </div>
          ) : null}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setIsCountModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => totalCountMutation.mutate()} disabled={totalCountMutation.isPending}>
              {totalCountMutation.isPending ? "Counting..." : "Run count"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminGigsPanel;
